(() => {
  const ROOT_SELECTOR = '[data-razza-early-access]';
  const TRIGGER_SELECTOR = '[data-razza-early-access-trigger]';
  const BUTTONS_SELECTOR = '[data-razza-early-access-product-buttons]';

  const SESSION_KEY = 'razzaEarlyAccessUnlocked';
  const PENDING_PRODUCT_KEY = 'razzaEarlyAccessPendingProduct';

  const root = document.querySelector(ROOT_SELECTOR);

  if (!root || root.dataset.initialized === 'true') return;

  root.dataset.initialized = 'true';

  const config = {
    codeHash: root.dataset.codeHash || '',
    closeDelay: Number(root.dataset.closeDelay || 1800),
    popupTitle: root.dataset.popupTitle || 'Acceso anticipado',
    popupText: root.dataset.popupText || 'Introduce tu código para acceder al drop.',
    closedTitle: root.dataset.closedTitle || 'Acceso todavía cerrado',
    closedText:
      root.dataset.closedText ||
      'Este producto todavía no está disponible para acceso anticipado. Vuelve cuando empiece el drop.',
    errorText: root.dataset.errorText || 'Código incorrecto',
    successAddingText: root.dataset.successAddingText || 'Código correcto. Añadiendo al carrito…',
    successChooseSizeText:
      root.dataset.successChooseSizeText || 'Código correcto. Selecciona tu talla para añadir al carrito.',
  };

  const title = root.querySelector('[data-razza-early-access-title]');
  const text = root.querySelector('[data-razza-early-access-text]');
  const form = root.querySelector('[data-razza-early-access-form]');
  const input = root.querySelector('[data-razza-early-access-input]');
  const message = root.querySelector('[data-razza-early-access-message]');
  const closeButtons = Array.from(root.querySelectorAll('[data-razza-early-access-close]'));

  let activeTrigger = null;
  let activeProductForm = null;
  let closeTimer = null;
  let isSubmitting = false;

  const normalizeCode = (value) => String(value || '').trim().toUpperCase();

  const sha256 = async (value) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(normalizeCode(value));
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const parseDate = (value) => {
    if (!value) return NaN;
    return Date.parse(String(value).replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
  };

  const getComputedAccessState = (trigger) => {
    if (!trigger) return 'unlocked';

    const startTime = parseDate(trigger.dataset.razzaStartAt);
    const unlockTime = parseDate(trigger.dataset.razzaUnlockAt);
    const now = Date.now();

    if (!Number.isNaN(unlockTime) && now >= unlockTime) {
      return 'unlocked';
    }

    if (!Number.isNaN(startTime) && now < startTime) {
      return 'closed';
    }

    return 'code-active';
  };

  const isUnlocked = () => {
    return window.sessionStorage.getItem(SESSION_KEY) === 'true';
  };

  const unlockSession = () => {
    window.sessionStorage.setItem(SESSION_KEY, 'true');
    document.documentElement.classList.add('razza-early-access-unlocked');
  };

  const setPendingProduct = (productId) => {
    if (!productId) return;
    window.sessionStorage.setItem(PENDING_PRODUCT_KEY, String(productId));
  };

  const getPendingProduct = () => {
    return window.sessionStorage.getItem(PENDING_PRODUCT_KEY);
  };

  const clearPendingProduct = () => {
    window.sessionStorage.removeItem(PENDING_PRODUCT_KEY);
  };

  const getVariantSelectedKey = (productId) => {
    if (!productId) return '';
    return `razzaEarlyAccessVariantSelected:${productId}`;
  };

  const clearVariantSelection = (productId, container) => {
    if (container) {
      delete container.dataset.razzaVariantSelected;
    }

    const key = getVariantSelectedKey(productId);

    if (key) {
      window.sessionStorage.removeItem(key);
    }
  };

  const markVariantSelected = (productId, container) => {
    if (container) {
      container.dataset.razzaVariantSelected = 'true';
    }

    const key = getVariantSelectedKey(productId);

    if (key) {
      window.sessionStorage.setItem(key, 'true');
    }
  };

  const hasStoredVariantSelection = (productId) => {
    const key = getVariantSelectedKey(productId);
    return key && window.sessionStorage.getItem(key) === 'true';
  };

  const showMessage = (value, type = 'success') => {
    if (!message) return;

    message.textContent = value || '';
    message.classList.remove('is-error', 'is-success', 'is-visible');

    if (value) {
      message.classList.add(type === 'error' ? 'is-error' : 'is-success', 'is-visible');
    }
  };

  const clearMessage = () => {
    showMessage('');
  };

  const setPopupMode = (mode) => {
    const isClosed = mode === 'closed';

    root.classList.toggle('is-closed', isClosed);

    if (title) {
      title.textContent = isClosed ? config.closedTitle : config.popupTitle;
    }

    if (text) {
      text.textContent = isClosed ? config.closedText : config.popupText;
    }

    if (form) {
      form.hidden = isClosed;
    }
  };

  const findProductForm = (element) => {
    if (!element) return null;

    const directForm = element.closest('form[action*="/cart/add"]');

    if (directForm) return directForm;

    const component = element.closest('product-form-component');

    if (component) {
      return component.querySelector('form[action*="/cart/add"]');
    }

    const scope = element.closest('.shopify-section, product-info, main-product, [data-product-id]');

    if (scope) {
      return scope.querySelector('form[action*="/cart/add"]');
    }

    return document.querySelector('form[action*="/cart/add"]');
  };

  const findProductScope = (element, productForm) => {
    return (
      element?.closest('.shopify-section, product-info, main-product, [data-product-id]') ||
      productForm?.closest('.shopify-section, product-info, main-product, [data-product-id]') ||
      null
    );
  };

  const getVariantInput = (productForm) => {
    if (!productForm) return null;
    return productForm.querySelector('input[name="id"], select[name="id"]');
  };

  const getVariantId = (productForm) => {
    const variantInput = getVariantInput(productForm);
    return variantInput && !variantInput.disabled ? String(variantInput.value || '') : '';
  };

  const hasOnlyDefaultVariant = (trigger) => {
    return trigger?.dataset.razzaSingleVariant === 'true';
  };

  const requiresManualVariantChoice = (trigger) => {
    return trigger?.dataset.razzaRequiresVariantChoice === 'true';
  };

  const hasUserSelectedVariant = (trigger, productForm) => {
    if (!trigger) return false;

    if (!requiresManualVariantChoice(trigger)) return true;
    if (hasOnlyDefaultVariant(trigger)) return true;

    const productId = trigger.dataset.razzaProductId || '';
    const scope = findProductScope(trigger, productForm);

    if (hasStoredVariantSelection(productId)) return true;

    return scope?.dataset.razzaVariantSelected === 'true';
  };

  const hasValidVariantReady = (trigger, productForm) => {
    const variantId = getVariantId(productForm);

    if (!variantId) return false;
    if (!hasUserSelectedVariant(trigger, productForm)) return false;

    return true;
  };

  const submitProductForm = (productForm) => {
    if (!productForm || isSubmitting) return;

    isSubmitting = true;

    const tempButton = document.createElement('button');
    tempButton.type = 'submit';
    tempButton.name = 'add';
    tempButton.hidden = true;
    tempButton.setAttribute('aria-hidden', 'true');

    productForm.appendChild(tempButton);

    if (typeof productForm.requestSubmit === 'function') {
      productForm.requestSubmit(tempButton);
    } else {
      tempButton.click();
    }

    window.setTimeout(() => {
      tempButton.remove();
      isSubmitting = false;
    }, 900);
  };

  const markProductUnlocked = (trigger) => {
    if (!trigger) return;

    trigger.dataset.razzaEarlyAccessUnlocked = 'true';

    const productId = trigger.dataset.razzaProductId || '';
    const productForm = findProductForm(trigger);
    const scope = findProductScope(trigger, productForm);

    if (scope) {
      scope.classList.add('razza-early-access-is-unlocked');
    }

    if (productId) {
      document
        .querySelectorAll(`${BUTTONS_SELECTOR}[data-razza-product-id="${productId}"]`)
        .forEach((element) => {
          element.classList.add('razza-early-access-is-unlocked');
          element.dataset.razzaAccessState = 'unlocked';
        });
    }
  };

  const updateTriggerState = (trigger) => {
    if (!trigger) return 'unlocked';

    const state = getComputedAccessState(trigger);
    const productId = trigger.dataset.razzaProductId || '';

    trigger.dataset.razzaAccessState = state;

    if (productId) {
      document
        .querySelectorAll(`${BUTTONS_SELECTOR}[data-razza-product-id="${productId}"]`)
        .forEach((element) => {
          element.dataset.razzaAccessState = state;
          element.classList.toggle('razza-early-access-is-closed', state === 'closed');
          element.classList.toggle('razza-early-access-code-active', state === 'code-active');

          if (state === 'unlocked') {
            element.classList.add('razza-early-access-is-unlocked');
          }
        });
    }

    return state;
  };

  const openPopup = (trigger, mode = 'code-active') => {
    activeTrigger = trigger;
    activeProductForm = findProductForm(trigger);

    clearTimeout(closeTimer);
    clearMessage();
    setPopupMode(mode);

    root.hidden = false;
    document.body.classList.add('razza-early-access-open');

    if (mode !== 'closed') {
      window.requestAnimationFrame(() => {
        input?.focus();
      });
    }
  };

  const closePopup = () => {
    root.hidden = true;
    document.body.classList.remove('razza-early-access-open');

    if (input) {
      input.value = '';
    }

    clearMessage();
    setPopupMode('code-active');

    activeTrigger = null;
    activeProductForm = null;
    isSubmitting = false;
  };

  const closePopupAfterDelay = () => {
    clearTimeout(closeTimer);
    closeTimer = window.setTimeout(closePopup, Math.min(config.closeDelay, 900));
  };

  const handleCorrectCode = () => {
    const trigger = activeTrigger;
    const productForm = activeProductForm || findProductForm(trigger);
    const productId = trigger?.dataset.razzaProductId || '';
    const scope = findProductScope(trigger, productForm);

    unlockSession();
    markProductUnlocked(trigger);

    if (hasOnlyDefaultVariant(trigger) && getVariantId(productForm)) {
      showMessage(config.successAddingText, 'success');

      window.setTimeout(() => {
        closePopup();
        submitProductForm(productForm);
      }, 180);

      return;
    }

    clearVariantSelection(productId, scope);
    setPendingProduct(productId);

    showMessage(config.successChooseSizeText, 'success');
    closePopupAfterDelay();
  };

  const handleWrongCode = () => {
    showMessage(config.errorText, 'error');
    closePopupAfterDelay();
  };

  const handlePopupSubmit = async (event) => {
    event.preventDefault();

    if (!activeTrigger) return;

    const state = updateTriggerState(activeTrigger);

    if (state === 'closed') return;

    if (!input || !config.codeHash || config.codeHash === 'pending') {
      handleWrongCode();
      return;
    }

    const code = normalizeCode(input.value);

    if (!code) {
      handleWrongCode();
      return;
    }

    input.value = code;

    const submitButton = form?.querySelector('button[type="submit"]');

    if (submitButton) {
      submitButton.disabled = true;
    }

    try {
      const hash = await sha256(code);

      if (hash === String(config.codeHash || '').trim().toLowerCase()) {
        handleCorrectCode();
      } else {
        handleWrongCode();
      }
    } catch (error) {
      handleWrongCode();
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  };

  const handleTriggerClick = (event) => {
    const trigger = event.target.closest(TRIGGER_SELECTOR);

    if (!trigger) return;

    const state = updateTriggerState(trigger);
    const productForm = findProductForm(trigger);

    if (state === 'closed') {
      event.preventDefault();
      event.stopPropagation();
      openPopup(trigger, 'closed');
      return;
    }

    if (state === 'unlocked') {
      return;
    }

    if (state === 'code-active' && isUnlocked()) {
      event.preventDefault();
      event.stopPropagation();

      markProductUnlocked(trigger);

      if (hasOnlyDefaultVariant(trigger) && getVariantId(productForm)) {
        submitProductForm(productForm);
        return;
      }

      if (hasValidVariantReady(trigger, productForm)) {
        submitProductForm(productForm);
        return;
      }

      setPendingProduct(trigger.dataset.razzaProductId || '');
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openPopup(trigger, 'code-active');
  };

  const isVariantSelectionEvent = (event) => {
    const target = event.target;

    if (!target) return false;
    if (target.closest(TRIGGER_SELECTOR)) return false;

    const directVariantControl = target.matches(
      [
        'input[type="radio"][name^="options"]',
        'input[type="radio"][name*="option"]',
        'input[type="radio"][name*="Option"]',
        'input[type="radio"][name*="Talla"]',
        'input[type="radio"][name*="Size"]',
        'select[name^="options"]',
        'select[name*="option"]',
        'select[name*="Option"]',
        'select[name*="Talla"]',
        'select[name*="Size"]',
        'select[name="id"]',
      ].join(',')
    );

    if (directVariantControl) return true;

    const variantElement = target.closest(
      [
        'variant-picker',
        'variant-selects',
        '.variant-picker',
        '.product-form__input',
        '[data-option-value]',
        '[data-variant-id]',
        '[data-product-option-value]',
        'label',
      ].join(',')
    );

    if (!variantElement) return false;
    if (variantElement.closest(TRIGGER_SELECTOR)) return false;

    const scope = variantElement.closest('.shopify-section, product-info, main-product, [data-product-id]');

    if (!scope) return false;

    return Boolean(scope.querySelector(TRIGGER_SELECTOR));
  };

  const tryAutoAddPendingProduct = (trigger, productForm) => {
    if (!trigger || !productForm) return;

    const productId = trigger.dataset.razzaProductId || '';
    const pendingProductId = getPendingProduct();

    if (
      isUnlocked() &&
      pendingProductId &&
      productId &&
      String(pendingProductId) === String(productId) &&
      hasValidVariantReady(trigger, productForm)
    ) {
      clearPendingProduct();

      window.setTimeout(() => {
        markProductUnlocked(trigger);
        submitProductForm(productForm);
      }, 80);
    }
  };

  const handleVariantInteraction = (event) => {
    if (!isVariantSelectionEvent(event)) return;

    const target = event.target;
    const container = target.closest('.shopify-section, product-info, main-product, [data-product-id]');

    if (!container) return;

    const productForm =
      target.closest('form[action*="/cart/add"]') ||
      container.querySelector('form[action*="/cart/add"]');

    if (!productForm) return;

    const trigger =
      container.querySelector(TRIGGER_SELECTOR) ||
      productForm.querySelector(TRIGGER_SELECTOR);

    if (!trigger) return;

    const state = updateTriggerState(trigger);

    if (state === 'closed') return;

    const productId = trigger.dataset.razzaProductId || container.dataset.productId || '';

    markVariantSelected(productId, container);

    [40, 120, 240].forEach((delay) => {
      window.setTimeout(() => {
        markVariantSelected(productId, container);
        tryAutoAddPendingProduct(trigger, productForm);
      }, delay);
    });
  };

  const watchVariantInputs = () => {
    document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
      const productForm = findProductForm(trigger);
      const inputEl = getVariantInput(productForm);

      if (!inputEl || inputEl.dataset.razzaWatched === 'true') return;

      inputEl.dataset.razzaWatched = 'true';

      let lastValue = inputEl.value;

      const check = () => {
        if (inputEl.value === lastValue) return;

        lastValue = inputEl.value;

        const container = findProductScope(trigger, productForm);
        const productId = trigger.dataset.razzaProductId || container?.dataset.productId || '';

        markVariantSelected(productId, container);
        tryAutoAddPendingProduct(trigger, productForm);
      };

      inputEl.addEventListener('change', check);

      const interval = window.setInterval(() => {
        if (!document.body.contains(inputEl)) {
          window.clearInterval(interval);
          return;
        }

        check();
      }, 150);
    });
  };

  const refreshTriggers = () => {
    document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
      const state = updateTriggerState(trigger);

      if (state === 'unlocked') {
        markProductUnlocked(trigger);
      } else if (state === 'code-active' && isUnlocked()) {
        markProductUnlocked(trigger);
      }
    });

    watchVariantInputs();
  };

  closeButtons.forEach((button) => {
    button.addEventListener('click', closePopup);
  });

  form?.addEventListener('submit', handlePopupSubmit);

  document.addEventListener('click', handleTriggerClick, true);
  document.addEventListener('change', handleVariantInteraction, true);
  document.addEventListener('click', handleVariantInteraction, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !root.hidden) {
      closePopup();
    }
  });

  if (isUnlocked()) {
    document.documentElement.classList.add('razza-early-access-unlocked');
  }

  refreshTriggers();

  window.setInterval(refreshTriggers, 15000);

  window.RazzaEarlyAccess = {
    isUnlocked,
    unlockSession,
    refresh: refreshTriggers,
    clear: () => {
      window.sessionStorage.removeItem(SESSION_KEY);
      window.sessionStorage.removeItem(PENDING_PRODUCT_KEY);

      Object.keys(window.sessionStorage)
        .filter((key) => key.startsWith('razzaEarlyAccessVariantSelected:'))
        .forEach((key) => window.sessionStorage.removeItem(key));

      document.documentElement.classList.remove('razza-early-access-unlocked');

      document.querySelectorAll('.razza-early-access-is-unlocked').forEach((element) => {
        element.classList.remove('razza-early-access-is-unlocked');
      });
    },
  };
})();